import { useState, useEffect } from "react";
import myDocumentsIcon from "../assets/images/my-documents-icon.png";
import licenseDefaultImg from "../assets/images/license.png";

interface Document {
  id: string;
  type: string;
  icon?: string;
  title: string;
  description: string;
}

interface DocumentCardProps {
  item: Document;
  onShare: () => void;
  isActive?: boolean;
}

function DocumentCard({ item, onShare }: DocumentCardProps) {
  const [hovered, setHovered] = useState(false);
  const active = hovered;
  const icon = item.icon ?? licenseDefaultImg;

  return (
    <div
      className="flex items-center justify-between px-4 py-3 rounded-[3px]"
      style={{
        transition: "background 150ms ease, border-color 150ms ease",
        ...(active
          ? {
              background: "linear-gradient(90deg, #0e2a4a 0%, #091828 100%)",
              border: "1px solid #137fe3",
            }
          : {
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.07)",
            }),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Left: icon + info */}
      <div className="flex items-center gap-3">
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-[4px] overflow-hidden"
          style={{
            width: 64,
            height: 50,
            background: "#090c12",
            border: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <img
            src={icon}
            alt={item.title}
            className="object-contain w-full h-full"
            draggable={false}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-white text-[16px] leading-none bender-font">
            {item.title}
          </span>
          <span className="text-[12px] leading-none text-white/50">
            {item.description}
          </span>
        </div>
      </div>

      {/* Right: Share button */}
      <button
        className="cursor-pointer outline-none rounded-[3px] text-[14px] purista-font"
        style={{
          width: 178,
          padding: "11px 0",
          textAlign: "center",
          transition:
            "background 150ms ease, border-color 150ms ease, color 150ms ease",
          ...(active
            ? {
                background: "linear-gradient(180deg, #0f2d4a 0%, #091e38 100%)",
                border: "1px solid #137fe3",
                color: "#ffffff",
              }
            : {
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.50)",
              }),
        }}
        onClick={onShare}
      >
        Share to Player
      </button>
    </div>
  );
}

const TABS = ["Allowed", "Licenses", "Medicals"] as const;
type Tab = (typeof TABS)[number];

function makeItems(tab: Tab): Document[] {
  return Array.from({ length: 5 }, (_, i) => ({
    id: `${tab}-${i}`,
    type: tab.toLowerCase(),
    title: "License name",
    description: "Expires in: 32 days",
  }));
}

const TAB_DATA: Record<Tab, Document[]> = {
  Allowed: makeItems("Allowed"),
  Licenses: makeItems("Licenses"),
  Medicals: makeItems("Medicals"),
};

interface Props {
  onClose: () => void;
}

export function MyDocumentsModal({ onClose }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>("Allowed");
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 220);
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const isIn = visible && !closing;
  const items = TAB_DATA[activeTab];

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center purista-font"
      style={{
        background: "rgba(0,0,0,0.88)",
        opacity: isIn ? 1 : 0,
        transition: "opacity 200ms ease",
      }}
      onClick={handleClose}
    >
      {/* Modal card */}
      <div
        className="flex flex-col overflow-hidden purista-font"
        style={{
          width: 900,
          background: "#08090A",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 10,
          opacity: isIn ? 1 : 0,
          transform: isIn
            ? "scale(1) translateY(0px)"
            : "scale(0.96) translateY(14px)",
          transition:
            "opacity 220ms ease, transform 220ms cubic-bezier(0.22,1,0.36,1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex-shrink-0 flex items-center justify-center rounded-[6px]"
              style={{ width: 52, height: 52, background: "#1a4580" }}
            >
              <img
                src={myDocumentsIcon}
                alt=""
                className="w-8 h-8 object-contain"
                draggable={false}
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-white uppercase text-[20px] leading-tight">
                My Documents
              </span>
              <span className="uppercase text-[13px] tracking-wider leading-none text-white/50">
                Management of items on same place
              </span>
            </div>
          </div>

          {/* ESC / CLOSE — identical to inventory header */}
          <button
            onClick={handleClose}
            className="flex items-center cursor-pointer outline-none rounded-[6px] overflow-hidden py-2 px-3 space-x-3 bg-[#FF1F1F]/5 min-w-[90px]"
            style={{ border: "1px solid #7a1919" }}
          >
            <span className="text-[13px] bg-[#FF1F1F]/15 tracking-widest purista-font text-[#FF1F1F] p-[2px] px-[3px] rounded-sm">
              ESC
            </span>
            <span className="text-white text-[14px] uppercase tracking-wider purista-font">
              CLOSE
            </span>
          </button>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.07)" }} />

        {/* Tabs */}
        <div className="flex gap-1.5 px-4 pt-4 pb-3">
          {TABS.map((tab) => (
            <button
              key={tab}
              className="flex-1 text-[16px] cursor-pointer outline-none rounded-[6px] transition-colors duration-150"
              style={{
                height: 52,
                ...(activeTab === tab
                  ? {
                      background:
                        "linear-gradient(180deg, #0d1e35 0%, #081525 100%)",
                      border: "1px solid #137fe3",
                      color: "#ffffff",
                    }
                  : {
                      background:
                        "linear-gradient(90deg, #0e0f0f 0%, #121313 50%, #0e0f0f 100%)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      color: "rgba(255,255,255,0.40)",
                    }),
              }}
              onClick={() => {
                setActiveTab(tab);
                setActiveItemId(null);
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Document list */}
        <div className="flex flex-col gap-2 px-4 pb-4">
          {items.map((item) => (
            <DocumentCard
              key={item.id}
              item={item}
              isActive={item.id === activeItemId}
              onShare={() =>
                setActiveItemId((prev) => (prev === item.id ? null : item.id))
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}
